<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useInactivityTimer, type InactivityAuthStore } from '@peanuds/composables';
import { reportAuthFlowBlocked } from '@peanuds/shared/observability/runtime-observability';

import { useIsLoginActivityRoute } from '../composables/useIsLoginActivityRoute';
import SessionExpiredDialog from './SessionExpiredDialog.vue';
import { UiButton } from '@peanuds/ui';

interface Props {
  authStore: InactivityAuthStore;
}

const { authStore } = defineProps<Props>();

const inactivityTooltip = `This timer helps ensure your security by checking for activity. If you're inactive for 5 minutes,
we'll ask for a quick re-authentication to keep your account safe.`;

const inactivityTooltipOptions = {
  value: inactivityTooltip,
  autoHide: false,
};
const INACTIVITY_TIMER_DISPLAY_THRESHOLD_SECONDS = 60;

const { inactivityTime, hasSessionDeadline, isSessionExpiringSoon, isSessionExpired } =
  useInactivityTimer({
    authStore,
  });

const isLoginActivityRoute = useIsLoginActivityRoute();

const shouldShowInactivityTimer = computed(() => {
  const remainingSeconds = inactivityTime.value;

  return (
    authStore.isLoggedIn &&
    hasSessionDeadline.value &&
    remainingSeconds !== null &&
    remainingSeconds > 0 &&
    remainingSeconds <= INACTIVITY_TIMER_DISPLAY_THRESHOLD_SECONDS &&
    !isLoginActivityRoute.value
  );
});

const isSessionExpiredDialogVisible = computed(() => {
  return authStore.isLoggedIn && isSessionExpired.value && !isLoginActivityRoute.value;
});

const isSignInRedirectLoading = ref(false);

const handleContinueToSignIn = async () => {
  if (isSignInRedirectLoading.value) {
    return;
  }

  isSignInRedirectLoading.value = true;

  try {
    reportAuthFlowBlocked({
      operation: 'inactivity-timer',
      failureKind: 'session-expired',
      tags: {
        hasSessionDeadline: hasSessionDeadline.value,
      },
    });

    await authStore.redirectToLogin({ mode: 'resume' });
  } catch (error) {
    console.error(
      '[InactivityTimer] Failed to continue to sign in after session expiration',
      error,
    );
    isSignInRedirectLoading.value = false;
  }
};

watch(isSessionExpiredDialogVisible, (visible) => {
  if (!visible) {
    isSignInRedirectLoading.value = false;
  }
});

const formattedRemainingTime = computed(() => {
  const total = Math.max(0, Math.floor(inactivityTime.value ?? 0));
  const m = Math.floor(total / 60)
    .toString()
    .padStart(2, '0');
  const s = (total % 60).toString().padStart(2, '0');

  return `${m}:${s}`;
});
</script>

<template>
  <div v-if="shouldShowInactivityTimer" class="inactivity-timer">
    <div
      class="inactivity-timer__banner"
      :class="{ 'inactivity-timer__banner--expiring-soon': isSessionExpiringSoon }"
    >
      <div v-once class="inactivity-timer__summary">
        <UiButton
          v-tooltip.top="inactivityTooltipOptions"
          type="button"
          class="inactivity-timer__tooltip-button"
          aria-label="Inactivity timer information"
        >
          <template #icon>
            <i class="pi pi-question-circle inactivity-timer__tooltip-icon" aria-hidden="true" />
          </template>
        </UiButton>
        <span class="inactivity-timer__title">Inactivity Timer</span>
      </div>

      <span class="inactivity-timer__value">{{ formattedRemainingTime }}</span>
    </div>
  </div>

  <SessionExpiredDialog
    :visible="isSessionExpiredDialogVisible"
    :is-sign-in-redirect-loading="isSignInRedirectLoading"
    @continue="handleContinueToSignIn"
  />
</template>

<style scoped lang="scss">
.inactivity-timer {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 999;
  display: flex;
  font-family: var(--font-family-base);

  &:hover,
  &:focus-within {
    .inactivity-timer__banner {
      background: color-mix(in srgb, var(--p-surface-0) 60%, transparent);
    }

    .inactivity-timer__value {
      background: var(--p-surface-200);
    }

    .inactivity-timer__banner--expiring-soon .inactivity-timer__value {
      background: var(--p-red-300);
    }
  }

  &__banner {
    display: flex;
    align-items: center;
    gap: $space-md;
    padding: $space-xs $space-md;
    border-radius: 0 var(--p-border-radius-xxxl, 24px) 0 0;
    background: color-mix(in srgb, var(--p-surface-0) 40%, transparent);
    backdrop-filter: blur(4px);
    transition: background 0.2s ease;
  }

  &__summary {
    display: flex;
    align-items: center;
    gap: $static-space-1;
    color: var(--p-surface-500);
  }

  &__title {
    font-size: $text-sm;
    font-weight: $font-weight-regular;
    line-height: 1.2;
    letter-spacing: -0.004em;
  }

  &__tooltip-button {
    border: 0;
    padding: 0;
    background: transparent;
    color: var(--p-surface-400);
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover,
    &:focus-visible {
      color: var(--p-surface-600);
      outline: none;
    }
  }

  &__tooltip-icon {
    font-size: $static-text-md;
  }

  &__value {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 4.5rem;
    padding: $space-xs $space-md;
    border-radius: 999px;
    background: var(--p-surface-100);
    color: var(--p-surface-700);
    font-size: $text-sm;
    font-weight: $font-weight-medium;
    line-height: 1;
    letter-spacing: -0.004em;
    font-variant-numeric: tabular-nums;
    transition: background 0.2s ease;
  }

  &__banner--expiring-soon &__value {
    background: var(--p-red-300);
  }
}
</style>
